import * as React from 'react';
import {ChevronDown, Search} from 'lucide-react';
import {useVirtualizer} from '@tanstack/react-virtual';
import {type ReactNode, useCallback} from 'react';
import type {AirbnbSearchableSelectProps, AirbnbSearchableSelectValue} from './types';
import {Drawer, DrawerContent, DrawerDescription, DrawerTitle} from '../../drawer';
import {AirbnbFieldTrigger} from '../field-trigger';
import {useOutsideClick, useScreenResize, useEvent} from '../../hooks';
import {DEVICE} from '../../lib/device';
import type {SelectOption} from '../../lib/selector-option';
import {cn} from '../../lib/cn';

const ROW_HEIGHT = 48;
const DESKTOP_LIST_HEIGHT = 280;
const MOBILE_LIST_HEIGHT = 420;
const LOAD_MORE_THRESHOLD = 6;

function defaultFilter<T, V extends AirbnbSearchableSelectValue, L extends ReactNode>(
  option: SelectOption<T, V, L>,
  searchValue: string,
) {
  return String(option.label).toLowerCase().includes(searchValue.trim().toLowerCase());
}

const AirbnbSearchableSelectInternal = <
  T = unknown,
  V extends AirbnbSearchableSelectValue = string,
  L extends ReactNode = string,
>(
  {
    options,
    value,
    onChange,
    onBlur,
    onOpenChange,
    searchValue,
    onSearchChange,
    filterOption = defaultFilter,
    loading,
    hasNextPage,
    onLoadMore,
    label,
    topLabel,
    placeholder,
    searchPlaceholder = 'Search...',
    mobileTitle,
    getValueLabel,
    disabled,
    error,
    invalid,
    optional,
    tooltip,
    helper,
    hideErrorMessage,
    name,
    className,
    dropdownClassName,
    menuClassName,
    noOptionsMessage,
    loadingMessage,
  }: AirbnbSearchableSelectProps<T, V, L>,
  ref: React.ForwardedRef<HTMLButtonElement>,
) => {
  const {isMatch: isMobile} = useScreenResize(DEVICE.mobileXL);
  const reactId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [internalSearchValue, setInternalSearchValue] = React.useState('');
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listboxId = `${reactId}-listbox`;
  const labelId = `${reactId}-label`;
  const valueId = `${reactId}-value`;
  const helperTextId = `${reactId}-helper`;
  const errorId = `${reactId}-error`;
  const searchInputId = `${reactId}-search`;
  const effectiveSearchValue = searchValue ?? internalSearchValue;
  const shouldFilterLocally = !onSearchChange && filterOption !== null;
  const visibleOptions = React.useMemo(() => {
    if (!shouldFilterLocally || !effectiveSearchValue) {
      return options;
    }

    return options.filter(option => filterOption(option, effectiveSearchValue));
  }, [effectiveSearchValue, filterOption, options, shouldFilterLocally]);
  const selectedIndex = React.useMemo(
    () => visibleOptions.findIndex(option => option.value === value?.value),
    [value?.value, visibleOptions],
  );
  const helperText = placeholder ?? label;
  const valueLabel = value ? (getValueLabel?.(value) ?? String(value.label)) : undefined;
  const isBlocked = Boolean(disabled) || Boolean(loading);
  const triggerError = error ?? invalid;
  const describedBy = error && !hideErrorMessage ? errorId : undefined;
  const activeOptionId =
    highlightedIndex >= 0 ? getOptionId(reactId, highlightedIndex) : undefined;

  useOutsideClick({
    elementRef: containerRef,
    onOutsideClick: () => closeSelect(),
    isDisabled: !open || isMobile,
  });

  const handleOnOpenChange = useEvent(onOpenChange);
  const setSelectOpen = useCallback(
    (nextOpen: boolean, options?: {restoreFocus?: boolean}) => {
      setOpen(nextOpen);
      handleOnOpenChange?.(nextOpen);

      if (!nextOpen && options?.restoreFocus) {
        triggerRef.current?.focus();
      }
    },
    [handleOnOpenChange],
  );

  React.useEffect(() => {
    if (isBlocked) {
      setSelectOpen(false);
      return;
    }

    if (!open) return;

    const frameId = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isBlocked, open, setSelectOpen]);

  React.useEffect(() => {
    if (!open) {
      setHighlightedIndex(-1);
      return;
    }

    setHighlightedIndex(currentIndex => {
      if (
        currentIndex >= 0 &&
        currentIndex < visibleOptions.length &&
        !visibleOptions[currentIndex]?.isDisabled
      ) {
        return currentIndex;
      }

      return selectedIndex >= 0 ? selectedIndex : getFirstEnabledIndex(visibleOptions);
    });
  }, [open, selectedIndex, visibleOptions]);

  function openSelect() {
    if (isBlocked) return;

    setSelectOpen(true);
  }

  function closeSelect() {
    setSelectOpen(false, {restoreFocus: true});
  }

  function handleSearchChange(nextValue: string) {
    if (!onSearchChange) {
      setInternalSearchValue(nextValue);
    }

    onSearchChange?.(nextValue);
  }

  function handleSelect(option: SelectOption<T, V, L>) {
    if (isBlocked || option.isDisabled) return;

    onChange(option);
    setSelectOpen(false, {restoreFocus: true});
  }

  function moveHighlight(step: 1 | -1) {
    const startIndex =
      highlightedIndex >= 0
        ? highlightedIndex + step
        : step === 1
          ? 0
          : visibleOptions.length - 1;
    const nextIndex = getNextEnabledIndex(visibleOptions, startIndex, step);

    if (nextIndex >= 0) {
      setHighlightedIndex(nextIndex);
    }
  }

  function handleTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (isBlocked) return;

    if (
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp'
    ) {
      event.preventDefault();
      openSelect();
    }
  }

  function handleSearchKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeSelect();
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveHighlight(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = visibleOptions[highlightedIndex];

      if (option) {
        handleSelect(option);
      }
    }
  }

  const content = (
    <AirbnbSearchableSelectContent
      inputId={searchInputId}
      listboxId={listboxId}
      labelId={labelId}
      activeOptionId={activeOptionId}
      inputRef={inputRef}
      options={visibleOptions}
      value={value}
      searchValue={effectiveSearchValue}
      searchPlaceholder={searchPlaceholder}
      highlightedIndex={highlightedIndex}
      loading={loading}
      hasNextPage={hasNextPage}
      onLoadMore={onLoadMore}
      menuClassName={menuClassName}
      noOptionsMessage={noOptionsMessage}
      loadingMessage={loadingMessage}
      height={isMobile ? MOBILE_LIST_HEIGHT : DESKTOP_LIST_HEIGHT}
      idPrefix={reactId}
      onSearchChange={handleSearchChange}
      onSearchKeyDown={handleSearchKeyDown}
      onOptionClick={handleSelect}
      onOptionHover={setHighlightedIndex}
    />
  );

  React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement, []);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full max-w-[var(--max-field-width)]', className)}
    >
      {name && (
        <input type="hidden" name={name} value={value ? String(value.value) : ''} />
      )}
      <AirbnbFieldTrigger
        id={`${reactId}-trigger`}
        ref={triggerRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        label={label}
        topLabel={topLabel}
        labelId={labelId}
        valueId={valueId}
        helperTextId={helperTextId}
        errorId={error ? errorId : undefined}
        labelText={topLabel ? helperText : undefined}
        valueText={valueLabel}
        placeholder={helperText}
        describedBy={describedBy}
        error={triggerError}
        loading={loading}
        optional={optional}
        tooltip={tooltip}
        helper={helper}
        forceLabelText={Boolean(optional) || Boolean(tooltip)}
        hideErrorMessage={hideErrorMessage}
        disabled={disabled}
        onClick={() => {
          if (open) {
            closeSelect();
            return;
          }

          openSelect();
        }}
        onKeyDown={handleTriggerKeyDown}
        onBlur={onBlur}
        trailingAdornment={
          <ChevronDown
            className={cn(
              'h-6 w-6 text-[var(--chekin-airbnb-ink)] transition-transform',
              open && 'rotate-180',
            )}
          />
        }
      />
      {isMobile ? (
        <Drawer
          open={open}
          onOpenChange={nextOpen => {
            if (isBlocked && nextOpen) return;

            if (nextOpen) {
              setSelectOpen(true);
              return;
            }

            closeSelect();
          }}
        >
          <DrawerContent onClose={closeSelect} lockScroll={false}>
            <DrawerTitle className="sr-only">{mobileTitle ?? label}</DrawerTitle>
            <DrawerDescription className="sr-only">{label}</DrawerDescription>
            <div className="px-5 pb-5 pt-1">{content}</div>
          </DrawerContent>
        </Drawer>
      ) : open ? (
        <div
          className={cn(
            'absolute left-0 right-0 top-[calc(100%+8px)] z-20 overflow-hidden rounded-[20px] border border-[var(--chekin-airbnb-border)] bg-white shadow-[0_14px_30px_rgba(18,18,18,0.08)]',
            dropdownClassName,
          )}
        >
          {content}
        </div>
      ) : null}
    </div>
  );
};

type AirbnbSearchableSelectComponent = <
  T = unknown,
  V extends AirbnbSearchableSelectValue = string,
  L extends ReactNode = string,
>(
  props: AirbnbSearchableSelectProps<T, V, L> & React.RefAttributes<HTMLButtonElement>,
) => React.ReactElement | null;

const AirbnbSearchableSelect = React.forwardRef(
  AirbnbSearchableSelectInternal,
) as AirbnbSearchableSelectComponent;

type AirbnbSearchableSelectContentProps<
  T,
  V extends AirbnbSearchableSelectValue,
  L extends ReactNode,
> = {
  inputId: string;
  listboxId: string;
  labelId: string;
  activeOptionId?: string;
  inputRef: React.RefObject<HTMLInputElement>;
  options: SelectOption<T, V, L>[];
  value?: SelectOption<T, V, L> | null;
  searchValue: string;
  searchPlaceholder: string;
  highlightedIndex: number;
  loading?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  menuClassName?: string;
  noOptionsMessage?: () => string | undefined;
  loadingMessage?: () => string | undefined;
  height: number;
  idPrefix: string;
  onSearchChange: (value: string) => void;
  onSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onOptionClick: (option: SelectOption<T, V, L>) => void;
  onOptionHover: (index: number) => void;
};

function AirbnbSearchableSelectContent<
  T,
  V extends AirbnbSearchableSelectValue,
  L extends ReactNode,
>({
  inputId,
  listboxId,
  labelId,
  activeOptionId,
  inputRef,
  options,
  value,
  searchValue,
  searchPlaceholder,
  highlightedIndex,
  loading,
  hasNextPage,
  onLoadMore,
  menuClassName,
  noOptionsMessage,
  loadingMessage,
  height,
  idPrefix,
  onSearchChange,
  onSearchKeyDown,
  onOptionClick,
  onOptionHover,
}: AirbnbSearchableSelectContentProps<T, V, L>) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const lastLoadMoreOptionsLengthRef = React.useRef<number | null>(null);
  const previousHighlightedIndexRef = React.useRef(highlightedIndex);
  const rowCount = options.length + (loading && options.length > 0 ? 1 : 0);
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => listRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const emptyMessage = noOptionsMessage?.() ?? 'No matches found';
  const loadingText = loadingMessage?.() ?? 'Loading...';

  React.useEffect(() => {
    const lastItem = virtualItems[virtualItems.length - 1];
    const shouldLoadMore =
      !!lastItem &&
      hasNextPage &&
      !loading &&
      lastItem.index >= options.length - LOAD_MORE_THRESHOLD;

    if (shouldLoadMore && lastLoadMoreOptionsLengthRef.current !== options.length) {
      lastLoadMoreOptionsLengthRef.current = options.length;
      onLoadMore?.();
    }
  }, [hasNextPage, loading, onLoadMore, options.length, virtualItems]);

  React.useEffect(() => {
    const hasHighlightedIndexChanged =
      previousHighlightedIndexRef.current !== highlightedIndex;

    previousHighlightedIndexRef.current = highlightedIndex;

    if (highlightedIndex >= 0 && hasHighlightedIndexChanged) {
      virtualizer.scrollToIndex(highlightedIndex, {align: 'auto'});
    }
  }, [highlightedIndex, virtualizer]);

  return (
    <div className="p-2">
      <div className="relative mb-2">
        <Search
          aria-hidden="true"
          className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[var(--chekin-neutral-500)]"
        />
        <input
          id={inputId}
          ref={inputRef}
          role="combobox"
          aria-expanded="true"
          aria-controls={listboxId}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
          aria-labelledby={labelId}
          value={searchValue}
          placeholder={searchPlaceholder}
          onChange={event => onSearchChange(event.target.value)}
          onKeyDown={onSearchKeyDown}
          className="h-11 w-full rounded-xl border border-[var(--chekin-airbnb-border)] bg-white pl-12 pr-4 text-base text-[var(--chekin-airbnb-ink)] outline-none placeholder:text-[var(--chekin-neutral-500)] focus:border-[var(--chekin-airbnb-ink)]"
        />
      </div>
      {loading && options.length === 0 ? (
        <div className="px-4 py-5 text-center text-base leading-6 text-[var(--chekin-airbnb-gray-text)]">
          {loadingText}
        </div>
      ) : options.length === 0 ? (
        <div className="px-4 py-5 text-center text-base leading-6 text-[var(--chekin-airbnb-gray-text)]">
          {emptyMessage}
        </div>
      ) : (
        <div
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-labelledby={labelId}
          className={cn('overflow-y-auto outline-none', menuClassName)}
          style={{height: Math.min(height, rowCount * ROW_HEIGHT)}}
        >
          <div
            className="relative w-full"
            style={{height: `${virtualizer.getTotalSize()}px`}}
          >
            {virtualItems.map(virtualItem => {
              const option = options[virtualItem.index];

              if (!option) {
                return (
                  <div
                    key={virtualItem.key}
                    className="absolute left-0 top-0 flex w-full items-center px-4 text-base leading-6 text-[var(--chekin-airbnb-gray-text)]"
                    style={{
                      height: `${virtualItem.size}px`,
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    {loadingText}
                  </div>
                );
              }

              const isSelected = value?.value === option.value;
              const isHighlighted = virtualItem.index === highlightedIndex;

              return (
                <button
                  id={getOptionId(idPrefix, virtualItem.index)}
                  key={`${String(option.value)}-${virtualItem.index}`}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.isDisabled}
                  tabIndex={-1}
                  disabled={option.isDisabled}
                  onClick={() => onOptionClick(option)}
                  onMouseMove={() => onOptionHover(virtualItem.index)}
                  className={cn(
                    'absolute left-0 top-0 flex w-full items-center justify-center rounded-2xl px-4 text-center text-base leading-6 text-[var(--chekin-airbnb-zinc)] transition-colors',
                    isHighlighted && 'bg-[var(--chekin-airbnb-surface)]',
                    isSelected &&
                      'bg-[var(--chekin-airbnb-surface)] font-medium text-[var(--chekin-airbnb-ink-black)]',
                    option.isDisabled && 'cursor-not-allowed opacity-40',
                  )}
                  style={{
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <span className="truncate text-center">{String(option.label)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function getOptionId(idPrefix: string, index: number) {
  return `${idPrefix}-option-${index}`;
}

function getFirstEnabledIndex<
  T,
  V extends AirbnbSearchableSelectValue,
  L extends ReactNode,
>(options: SelectOption<T, V, L>[]) {
  return options.findIndex(option => !option.isDisabled);
}

function getNextEnabledIndex<
  T,
  V extends AirbnbSearchableSelectValue,
  L extends ReactNode,
>(options: SelectOption<T, V, L>[], startIndex: number, step: 1 | -1) {
  let nextIndex = startIndex;

  while (nextIndex >= 0 && nextIndex < options.length) {
    if (!options[nextIndex]?.isDisabled) {
      return nextIndex;
    }

    nextIndex += step;
  }

  return -1;
}

export {AirbnbSearchableSelect};
